/**
 * The error-reporting transport — docs/spec/06 §6, 09 §6, 10 §Q11.
 *
 * Sends already-redacted events to GitLab error tracking, which speaks the
 * Sentry ingestion protocol. Lives under `src/api/` because it performs a
 * `fetch` and HARD RULE 2 admits no exceptions; `lib/reporter.ts` stays
 * transport-agnostic and this plugs into its sink seam.
 *
 * ## Why no `@sentry/browser`
 *
 * Not (only) the dependency lock or the 200 KB budget (05 §5) — the decisive
 * reason is **PII**. An SDK earns its size by auto-capturing breadcrumbs: fetch
 * and XHR bodies, console output, DOM interactions, the full URL. In this app
 * those carry inmate names, addresses and scan content, and every one of them
 * would leave the browser before our redaction ever saw it (HARD RULE 7).
 *
 * A hand-written POST sends exactly the fields listed below and nothing else,
 * which is both smaller and the only version we can honestly claim is PII-free.
 *
 * ## The endpoint is derived, and it is not the obvious one
 *
 * GitLab's DSN path is not a bare project id:
 *
 *     https://<key>@observe.gitlab.com/errortracking/api/v1/projects/<projectId>
 *
 * The Sentry rule — last path segment is the project, everything before it is a
 * prefix — yields `…/projects/api/<projectId>/store/`, which reads like a
 * mistake and is correct. Verified against the live collector 2026-07-29:
 * that URL returns **200**, while `…/projects/<projectId>/store/` and
 * `…/envelope/` both return **401**.
 */
import type { ReportableEvent, ReportSink } from '../lib/reporter'

export interface ParsedDsn {
  /** Full ingestion URL to POST to. */
  readonly endpoint: string
  /** Public ingestion key — write-only, and public by design in a browser. */
  readonly key: string
}

/**
 * Split a Sentry-style DSN into the endpoint and key, or `null` when it is not
 * one.
 *
 * Returning `null` rather than throwing keeps a malformed value from taking down
 * the app on boot: reporting is diagnostics, and losing it must never cost the
 * operator their screen. `parseConfig` reports the malformed value separately so
 * it is loud in the place that can act on it.
 */
export function parseDsn(dsn: string): ParsedDsn | null {
  let url: URL
  try {
    url = new URL(dsn)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null

  const key = url.username
  if (key === '') return null

  const segments = url.pathname.split('/').filter((part) => part !== '')
  const projectId = segments.pop()
  if (projectId === undefined) return null

  const prefix = segments.length > 0 ? `/${segments.join('/')}` : ''
  return {
    endpoint: `${url.protocol}//${url.host}${prefix}/api/${projectId}/store/`,
    key,
  }
}

/** Identifies this client to the collector; no version, nothing sensitive. */
const CLIENT = 'pigeonly-physical-mail-web-client'

/**
 * The event body.
 *
 * **Everything here is either literal or already redacted.** Two deliberate
 * omissions:
 *
 * - **`location.search` is excluded — only `pathname` is sent.** The queue's
 *   free-text filter lives in the query string, so `?q=<name>` would ship a
 *   searched-for person's name to a third-party service. That is the exact leak
 *   HARD RULE 7 forbids, and it would arrive by accident rather than by anyone
 *   deciding to send it.
 * - **No token, ever** (06 §5). Nothing reads the auth store here.
 */
function buildBody(event: ReportableEvent, environment: string, now: number): string {
  return JSON.stringify({
    // The collector requires a 32-char hex id; derived from the clock and a
    // counter rather than randomness so it stays deterministic under test.
    event_id: eventId(now),
    timestamp: new Date(now).toISOString(),
    platform: 'javascript',
    level: event.kind === 'crash' ? 'error' : 'warning',
    logger: CLIENT,
    environment,
    message: event.message,
    tags: {
      kind: event.kind,
      ...(event.requestId === null ? {} : { request_id: event.requestId }),
    },
    // Path only, never `location.search` — see above.
    transaction: location.pathname,
    extra: event.context,
  })
}

let sequence = 0

function eventId(now: number): string {
  sequence = (sequence + 1) % 0xffff
  const stamp = now.toString(16).padStart(12, '0')
  const seq = sequence.toString(16).padStart(4, '0')
  return `${stamp}${seq}`.padEnd(32, '0').slice(0, 32)
}

export interface DsnSinkOptions {
  /** Injected in tests; defaults to the global. */
  readonly send?: typeof fetch | undefined
  readonly environment?: string | undefined
  readonly now?: (() => number) | undefined
}

/**
 * Build a sink that POSTs redacted events to the collector.
 *
 * Fire-and-forget and **incapable of throwing**: a diagnostics endpoint being
 * down, blocked by an ad blocker, or rejecting the payload must never surface to
 * an operator or break the code path that reported. The rejection is swallowed
 * for exactly that reason — there is nowhere better to report a failure to
 * report.
 *
 * `keepalive` so an event raised during unload still leaves; that is often the
 * most interesting one.
 */
export function createDsnSink(dsn: string, options: DsnSinkOptions = {}): ReportSink | null {
  const parsed = parseDsn(dsn)
  if (parsed === null) return null

  const send = options.send ?? globalThis.fetch
  const environment = options.environment ?? 'unknown'
  const now = options.now ?? Date.now

  return (event: ReportableEvent) => {
    try {
      void send(parsed.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Sentry-Auth': `Sentry sentry_version=7, sentry_key=${parsed.key}, sentry_client=${CLIENT}`,
        },
        body: buildBody(event, environment, now()),
        keepalive: true,
      }).catch(() => {
        // Deliberately silent — see above.
      })
    } catch {
      // A synchronous throw (no fetch at all) is equally not the operator's
      // problem.
    }
  }
}

/** Test-only: make `event_id` generation deterministic across cases. */
export function resetEventIdForTests(): void {
  sequence = 0
}
